import axios, { type AxiosResponse } from "axios";

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("doctor_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export interface OnboardingData {
  personalInfo: {
    firstName: string;
    lastName: string;
    phone: string;
    gender: string;
    dateOfBirth: string;
    country: string;
    address: string;
  };
  professionalInfo: {
    licenseNumber: string;
    specialization: string;
    yearsOfExperience: string;
    education: string;
    hospitalAffiliation?: string;
    bio?: string;
    documents?: File[];
  };
  preferences: {
    consultationFee?: string;
    availableHours: string[];
    emailNotifications: boolean;
    smsNotifications: boolean;
    appointmentReminders: boolean;
    profileVisibility: boolean;
    acceptNewPatients: boolean;
  };
}

export interface OnboardingStatus {
  doctor_id: string;
  onboarding_required: boolean;
  completion_percentage: number;
  completed_steps: {
    personal_info: boolean;
    professional_info: boolean;
    documents: boolean;
    preferences: boolean;
  };
  current_step: string;
  profile: {
    name: string;
    email: string;
    specialization: string;
    status: string;
    email_verified: boolean;
    created_at?: string;
  };
}

export class OnboardingService {
  async getStatus(): Promise<{ success: boolean; data: OnboardingStatus }> {
    try {
      const response: AxiosResponse = await api.get("/onboarding/status");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async completeOnboarding(
    data: OnboardingData,
  ): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response: AxiosResponse = await api.post(
        "/onboarding/complete",
        data,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateProfile(
    data: any,
  ): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response: AxiosResponse = await api.put(
        "/onboarding/update-profile",
        data,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async uploadDocuments(documents: {
    documents: any[];
  }): Promise<{ success: boolean; message: string; data: any }> {
    try {
      const response: AxiosResponse = await api.post(
        "/onboarding/upload-documents",
        documents,
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async resendVerification(
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse = await api.post(
        "/onboarding/resend-verification",
        { email },
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async verifyResetOtp(
    token: string,
    otp: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse = await api.post("/onboarding/verify-otp", {
        token,
        otp,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async resendResetOtp(
    token: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse = await api.post("/onboarding/resend-otp", {
        token,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse = await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }
}

// Export singleton instance
export const onboardingService = new OnboardingService();
export default onboardingService;
